import React, { useEffect, useState, useContext } from "react";
import axios from "axios"; // ✅ Import axios
import { UserContext } from "../../UserContext"; // ✅ Import User Context


const Meditation = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = "meditation"; // Hardcode category as 'meditation'
  const [videos, setVideos] = useState([]); // State to hold videos
  const [loading, setLoading] = useState(true); // State to handle loading

  // Fetch videos when the component mounts
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful
  const handleHelpfulClick = async (videoId) => {
    if (!user || !user.id) {
      alert("You must be logged in to mark as helpful.");
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error("Error marking video as helpful:", error);
    }
  };

  return (
    <div className="page-container meditation-page">
      <p class="disclaimer">
Disclaimer: As an Amazon Associate, I earn from qualifying purchases. If you make a purchase through the links here, 
I may earn a small commission at no extra cost to you. Thank you for your support.
</p>
<div className="micro-void"></div>
      <h1 className="page-title">Meditation and Mindfulness</h1>

      {/* Include the image */}
      <img
        src="/images/wf_meditate.webp" // Path to image in public folder
        alt="Person meditating"
        className="page-image"
        width="330"
      />
      <p className="page-description">
        Meditation is a practice that helps cultivate a deep sense of awareness, presence, and calm. 
        It involves focusing the mind, often through guided sessions, to quiet the thoughts and improve focus. 
        Regular meditation has been shown to reduce stress, improve emotional regulation, and enhance mental clarity.
      </p>

      {/* Health Benefits */}
      <h2 className="section-title">Health Benefits</h2>
      <p className="section-description">
        Regular meditation and mindfulness practices have numerous health benefits. These include:
      </p>
      <ul className="content-list">
        <li>Reduced stress and anxiety</li>
        <li>Improved focus and concentration</li>
        <li>Better emotional regulation</li>
        <li>Enhanced self-awareness and emotional resilience</li>
        <li>Improved sleep quality</li>
        <li>Reduced blood pressure</li>
      </ul>

      <hr className="section-divider" />
      

      {/* Additional Content (like Habit Plan, Apps, etc.) */}
      <div className="additional-content">
        <h2 className="section-title">Create a Habit Plan</h2>
        <p className="section-description">
          Building mindfulness into your daily routine can lead to long-lasting benefits. Here's a simple framework to help you design a habit plan:
        </p>
        <h3 className="content-subtitle">Framework for Building Mindfulness Habits:</h3>
        <ul className="content-list">
          <li>
            <strong>Start Small:</strong> Begin with manageable steps, such as a 2-minute breathing exercise or mindful eating during one meal.
          </li>
          <li>
            <strong>Attach to Existing Habits:</strong> Pair mindfulness with daily tasks. For example, practice deep breathing while waiting for your coffee to brew or reflect on gratitude while brushing your teeth.
          </li>
          <li>
            <strong>Track Your Progress:</strong> Use a journal, app, or simple checklist to track your mindfulness habit. Reflect on how you feel after consistent practice.
          </li>
        </ul>

        <h3 className="content-subtitle">Examples of Mindfulness Habits:</h3>
        <ul className="content-list">
          <li>Start each morning with 5 minutes of meditation before checking your phone.</li>
          <li>Take 3 deep breaths every time you feel stressed at work.</li>
          <li>Go for a mindful walk during lunch, focusing on the sights and sounds around you.</li>
          <li>Write down three things you’re grateful for before going to bed each night.</li>
        </ul>
        <p className="section-description">
          By incorporating small, intentional practices into your daily life, you can cultivate mindfulness habits that stick.
        </p>
      </div>

      <hr className="section-divider" />

      <h2 className="section-title">Curated Video Collection</h2>

      {/* If videos are still loading */}
      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>

                {/* ✅ Helpful Button */}
                <button
                  className="button"
                  onClick={() => handleHelpfulClick(video._id)}
                >
                  Mark as Helpful ({video.helpfulCount || 0})
                </button>
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}
    <hr className="section-divider" />
    <h2 className="section-title">Curated Book Collection</h2>
      <div className="book-card">
        <h3 className="book-title">The Miracle of Mindfulness</h3>
        
        {/* Book Cover Image from Open Library */}
        <img 
          src="https://covers.openlibrary.org/b/id/11442319-M.jpg" // Open Library cover image URL
          alt="The Miracle of Mindfulness by Thich Nhat Hanh" 
          className="book-cover" 
        />
        {/* Attribution for Image Source */}
        <p className="image-source">Image Source: Open Library API</p>
        <div className="micro-void"></div>
        <p class="book-details">
          <strong>Author:</strong> Thich Nhat Hanh <br />
          <div className="micro-void"></div>
          <strong>Overview:</strong> Written by the renowned Vietnamese Buddhist monk, this book is a profound yet simple 
          guide to mindfulness. Thich Nhat Hanh offers exercises, anecdotes, and stories that show how mindfulness can be 
          integrated into your daily activities, and how meditation can change your life. <br />
          <div className="barely-void"></div>
          <strong>Why It's Great:</strong> It's accessible and filled with wisdom for beginners and 
          experienced meditators alike. The teachings focus on kindness and compassion, making it perfect for those 
          seeking peace and insight.
        </p>
        
      <div className="micro-void"></div>
      <a
        href="https://amzn.to/4hpJgwX" // Your actual Amazon affiliate link
        className="amazon-link"
        target="_blank"
        rel="noopener noreferrer"
      >
      Buy on Amazon-https://amzn.to/4hpJgwX
      </a>
      {/* Affiliate Link Disclosure */}
      <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
      <div class="short-line"></div>

      </div>
      <div className="book-card">
        <h3 className="book-title">Wherever You Go, There You Are</h3>
        
        {/* Book Cover Image from Open Library */}
        <img 
          src="https://covers.openlibrary.org/b/id/545941-M.jpg" // Open Library cover image URL
          alt="Wherever You Go, There You Are by Jon Kabat-Zinn" 
          className="book-cover" 
        />
        {/* Attribution for Image Source */}
        <p className="image-source">Image Source: Open Library API</p>
        <div className="micro-void"></div>
        <p class="book-details">
    <strong>Author:</strong> Jon Kabat-Zinn <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> Kabat-Zinn is the creator of the Mindfulness-Based Stress Reduction (MBSR) program, and this book offers a simple yet profound guide to mindfulness meditation. The book explains mindfulness in a clear and direct way, emphasizing how it can be practiced in everyday life. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> It's a wonderful resource for those who want to understand mindfulness as a way of living, not just as a meditation technique. Kabat-Zinn’s warm and inviting approach makes it easy to start practicing mindfulness immediately.
  </p>
        
      <div className="micro-void"></div>
      <a
        href="https://amzn.to/4iBzV68" // Your actual Amazon affiliate link
        className="amazon-link"
        target="_blank"
        rel="noopener noreferrer"
      >
      Buy on Amazon-https://amzn.to/4iBzV68
      </a>
      {/* Affiliate Link Disclosure */}
      <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>

      </div>

      <hr className="section-divider" />

      <div className="additional-content">
        <h2 className="section-title">Popular Meditation and Mindfulness Apps</h2>
        <p className="section-description">
          Meditation and mindfulness apps have become essential tools for people looking to manage stress, improve well-being, and practice mindfulness in their daily lives. These apps provide guided meditations, breathing exercises, sleep aids, and mindfulness practices, making them accessible anytime and anywhere.
        </p>
        <h3 className="content-subtitle">Headspace</h3>
        <p>Headspace offers guided meditations focusing on relaxation, stress relief, and focus.</p>
        <h3 className="content-subtitle">Calm</h3>
        <p>Calm provides meditations and programs for improving sleep and relaxation.</p>
        <h3 className="content-subtitle">Insight Timer</h3>
        <p>Insight Timer has a large library of guided meditations and soundscapes for solo practice.</p>
      </div>

      <hr />

      {/* Forum Section */}
      <div className="forum-section">
        <h2 className="section-title">Join the Discussion</h2>
        <p className="section-description">
          Have questions or insights about meditation? Join our forum to connect with others who are exploring this topic.
        </p>
        <a href="/forum/Meditation" className="forum-link">
          Go to Meditation Forum
        </a>
      </div>
    </div>
  );
};

export default Meditation;
